<template>
  <div class="profile-page">
    <div class="page-shell">
      <div class="page-header">
        <div>
          <h1>Мой профиль</h1>
          <p>Просмотр основной информации об аккаунте</p>
        </div>
      </div>

      <Card class="profile-card">
        <template #content>
          <div v-if="loading" class="state-block">
            Загрузка...
          </div>

          <div v-else class="profile-content">
            <div v-if="permissionsStore.hasPermission('ManageCompanyLogo')" class="info-item">
              <CompanyLogoSettings />
            </div>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">Email</span>
                <span class="info-value">{{ user.email || "—" }}</span>
              </div>

              <div class="info-item">
                <span class="info-label">Имя</span>
                <span class="info-value">{{ user.fullName || "—" }}</span>
              </div>

              <div class="info-item">
                <span class="info-label">Тип клиента</span>
                <span class="info-value">{{ user.clientType || "—" }}</span>
              </div>

              <div class="info-item">
                <span class="info-label">Телефон</span>

                <div class="info-value info-value--action">
                  <template v-if="user.phoneNumber && !editingPhone">
                    <span>{{ user.phoneNumber }}</span>

                    <Button
                      label="Изменить"
                      size="small"
                      class="secondary-btn"
                      outlined
                      @click="startEditPhone"
                    />
                  </template>

                  <div v-else class="input-block">
                    <InputText
                      v-model="phoneInput"
                      @input="phoneError = ''"
                    />
                    <small class="hint">Международный формат, максимум 15 цифр</small>

                    <Button
                      label="Сохранить"
                      size="small"
                      :loading="savingPhone"
                      @click="savePhone"
                    />

                    <small v-if="phoneError" class="error">
                      {{ phoneError }}
                    </small>
                  </div>
                </div>
              </div>

              <template v-if="user.clientType === 'B2B'">
                <div class="info-item">
                  <span class="info-label">Компания</span>

                  <div class="info-value info-value--action">
                    <template v-if="!editingCompany">
                      <span>{{ user.companyName || "—" }}</span>

                      <Button
                        v-if="canEditCompanyDetails"
                        label="Изменить"
                        size="small"
                        class="secondary-btn"
                        outlined
                        @click="startEditCompany"
                      />
                    </template>

                    <div v-else class="input-block">
                      <InputText
                        v-model="companyInput"
                        placeholder="Введите название компании"
                        maxlength="30"
                      />
                      <small class="hint">Максимум 30 символов</small>

                      <Button
                        label="Сохранить"
                        size="small"
                        :loading="savingCompany"
                        @click="saveCompany"
                      />

                      <small v-if="companyError" class="error">
                        {{ companyError }}
                      </small>
                    </div>
                  </div>
                </div>

                <div class="info-item">
                  <span class="info-label">БИН</span>

                  <div class="info-value info-value--action">
                    <template v-if="!editingBin">
                      <span>{{ user.bin || "—" }}</span>

                      <Button
                        v-if="canEditCompanyDetails"
                        label="Изменить"
                        size="small"
                        class="secondary-btn"
                        outlined
                        @click="startEditBin"
                      />
                    </template>

                    <div v-else class="input-block">
                      <InputText
                        v-model="binInput"
                        placeholder="123456789012"
                        maxlength="12"
                        @input="validateBin"
                      />

                      <Button
                        label="Сохранить"
                        size="small"
                        :loading="savingBin"
                        @click="saveBin"
                      />

                      <small v-if="binError" class="error">
                        {{ binError }}
                      </small>
                    </div>
                  </div>
                </div>

                <div class="info-item info-item--full">
                  <span class="info-label">Адрес</span>

                  <div class="info-value info-value--action">
                    <template v-if="user.address && !editingAddress">
                      <span>{{ user.address }}</span>

                      <Button
                        label="Изменить"
                        size="small"
                        class="secondary-btn"
                        outlined
                        @click="startEditAddress"
                      />
                    </template>

                    <div v-else class="input-block">
                      <InputText
                        v-model="addressInput"
                        placeholder="Введите адрес"
                        maxlength="50"
                      />
                      <small class="hint">Максимум 50 символов</small>

                      <Button
                        label="Сохранить"
                        size="small"
                        :loading="savingAddress"
                        @click="saveAddress"
                      />

                      <small v-if="addressError" class="error">
                        {{ addressError }}
                      </small>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script setup>
import CompanyLogoSettings from "@/modules/cabinet/components/CompanyLogoSettings.vue"
import { useAuthStore } from "@/modules/auth/store/auth.store"
import { usePermissionsStore } from "@/modules/auth/store/permissions.store"
import { normalizePhone } from "@/shared/lib/phone"
import { computed, ref, onMounted } from "vue"
import { getMe, updateUser } from "@/modules/auth/api/user.service"
import { useAppToast } from "@/shared/lib/useAppToast"

import Card from "primevue/card"
import Button from "primevue/button"
import InputText from "primevue/inputtext"

const toast = useAppToast()
const authStore = useAuthStore()
const permissionsStore = usePermissionsStore()

const user = ref({})
const loading = ref(true)

const canEditCompanyDetails = computed(() => permissionsStore.hasPermission("EditCompanyDetails"))

const editingPhone = ref(false)
const editingAddress = ref(false)
const editingCompany = ref(false)
const editingBin = ref(false)

const savingPhone = ref(false)
const savingAddress = ref(false)
const savingCompany = ref(false)
const savingBin = ref(false)

const phoneInput = ref("")
const addressInput = ref("")
const companyInput = ref("")
const binInput = ref("")

const phoneError = ref("")
const addressError = ref("")
const companyError = ref("")
const binError = ref("")

function validateBin(event) {
  let value = event.target.value.replace(/\D/g, '')
  
  if (value.length > 12) {
    value = value.slice(0, 12)
  }
  
  binInput.value = value
}

const loadUser = async () => {
  loading.value = true

  try {
    const [userRes] = await Promise.all([getMe(), permissionsStore.fetchPermissions(true)])
    user.value = userRes.data
    authStore.setUser(userRes.data)

    phoneInput.value = user.value.phoneNumber || ""
    addressInput.value = user.value.address || ""
    companyInput.value = user.value.companyName || ""
    binInput.value = user.value.bin || ""

  } catch (error) {
    toast.error(error.message || "Не удалось загрузить профиль")
    user.value = {}
  } finally {
    loading.value = false
  }
}

onMounted(loadUser)

function startEditPhone() {
  phoneError.value = ""
  editingPhone.value = true
}

function startEditAddress() {
  addressError.value = ""
  editingAddress.value = true
}

const savePhone = async () => {
  phoneError.value = ""

  const phoneResult = normalizePhone(phoneInput.value)
  if (!phoneResult.isValid) {
    phoneError.value = phoneResult.message
    return
  }

  savingPhone.value = true

  try {
    await updateUser({
      phoneNumber: phoneResult.value
    })

    user.value = {
      ...user.value,
      phoneNumber: phoneResult.value
    }

    phoneInput.value = phoneResult.value
    toast.success("Телефон обновлён")
    authStore.setUser({ ...user.value, hasCompanyLogo: authStore.user?.hasCompanyLogo ?? user.value.hasCompanyLogo })
    editingPhone.value = false
  } catch (error) {
    phoneError.value = error.message || "Ошибка при сохранении телефона"
  } finally {
    savingPhone.value = false
  }
}

const saveAddress = async () => {
  addressError.value = ""

  if (!addressInput.value) {
    addressError.value = "Введите адрес"
    return
  }

  if (addressInput.value.length > 50) {
    addressError.value = "Адрес не может превышать 50 символов"
    return
  }

  savingAddress.value = true

  try {
    await updateUser({
      address: addressInput.value || null
    })

    user.value = {
      ...user.value,
      address: addressInput.value || null
    }

    toast.success("Адрес обновлён")
    authStore.setUser({ ...user.value, hasCompanyLogo: authStore.user?.hasCompanyLogo ?? user.value.hasCompanyLogo })
    editingAddress.value = false
  } catch (error) {
    addressError.value = error.message || "Ошибка при сохранении адреса"
  } finally {
    savingAddress.value = false
  }
}

function startEditCompany() {
  companyError.value = ""
  editingCompany.value = true
}

function startEditBin() {
  binError.value = ""
  editingBin.value = true
}

const saveCompany = async () => {
  companyError.value = ""

  if (!companyInput.value) {
    companyError.value = "Введите название компании"
    return
  }

  if (companyInput.value.length > 30) {
    companyError.value = "Адрес не может превышать 30 символов"
    return
  }

  savingCompany.value = true

  try {
    await updateUser({
      companyName: companyInput.value
    })

    user.value = {
      ...user.value,
      companyName: companyInput.value
    }

    toast.success("Название компании обновлено")
    authStore.setUser({ ...user.value, hasCompanyLogo: authStore.user?.hasCompanyLogo ?? user.value.hasCompanyLogo })
    editingCompany.value = false
  } catch (error) {
    companyError.value = error.message || "Ошибка при сохранении названия компании"
  } finally {
    savingCompany.value = false
  }
}

const saveBin = async () => {
  binError.value = ""

  if (!binInput.value) {
    binError.value = "Введите БИН"
    return
  }

  if (!/^\d{12}$/.test(binInput.value)) {
    binError.value = "БИН должен содержать ровно 12 цифр"
    return
  }

  savingBin.value = true

  try {
    await updateUser({
      bin: binInput.value
    })

    user.value = {
      ...user.value,
      bin: binInput.value
    }

    toast.success("БИН обновлён")
    authStore.setUser({ ...user.value, hasCompanyLogo: authStore.user?.hasCompanyLogo ?? user.value.hasCompanyLogo })
    editingBin.value = false
  } catch (error) {
    binError.value = error.message || "Ошибка при сохранении БИН"
  } finally {
    savingBin.value = false
  }
}
</script>

<style scoped>
.profile-page {
  min-height: 100vh;
  background: #fafafa;
}

.page-shell {
  max-width: 960px;
  margin: 0 auto;
  padding: 32px 24px 48px;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h1 {
  margin: 0;
  font-size: 36px;
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: #111111;
}

.page-header p {
  margin: 10px 0 0;
  font-size: 15px;
  color: #6b6b6b;
}

.profile-card {
  border: 1px solid #ececec;
  border-radius: 24px;
  box-shadow: 0 18px 40px rgba(17, 17, 17, 0.05);
  overflow: hidden;
}

:deep(.profile-card.p-card) {
  background: #ffffff;
}

:deep(.profile-card .p-card-content) {
  padding: 24px;
}

.state-block {
  padding: 28px;
  text-align: center;
  color: #777777;
  border: 1px dashed #d7d7d7;
  border-radius: 18px;
  background: #fafafa;
}

.profile-content {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px;
  border: 1px solid #ececec;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(17, 17, 17, 0.04);
  min-width: 0;
}

.info-item--full {
  grid-column: 1 / -1;
}

.info-label {
  font-size: 13px;
  font-weight: 600;
  color: #666666;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.info-value {
  font-size: 16px;
  font-weight: 500;
  color: #111111;
  line-height: 1.5;
  word-break: break-word;
}

.info-value--action {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.input-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.hint {
  font-size: 11px;
  color: #888;
}

.error {
  color: red;
  font-size: 0.85rem;
}

:deep(.secondary-btn.p-button) {
  background: #ffffff;
  border: 1px solid #d8d8d8;
  color: #111111;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.secondary-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

@media (max-width: 760px) {
  .page-shell {
    padding: 24px 16px 40px;
  }

  .page-header h1 {
    font-size: 30px;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .info-item--full {
    grid-column: auto;
  }

  .info-value--action {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
